import * as React from 'react';
import { translate } from 'react-i18next';

const { connect } = require('react-redux');
import { IStore } from 'redux/IStore';
import {
	deleteArticleComment,
	updateArticleCommentInList,
	reviewArticleComment,
	getArticleComments,
	getArticleCommentsSuccess,
} from 'modules/magazine/article/articlecomments';
import { showModal, closeModal } from 'modules/modal';
import { IArticleCommentsData, IArticleCommentsAction } from 'models/magazine/article/articlecomments';
import { IArticleComment } from 'models/magazine/article/articlecomment';
import { IUserPreferences } from 'models/user/userpreferences';
import { IModalAction } from 'models/modal';
import * as c from 'models/constants';
import { ErrorSummary } from 'components';
import ArticleComment from 'components/Magazine/Article/ArticleComment/ArticleComment';
import { IAddArticleComment } from 'models/magazine/article/addarticlecomment';

interface IProps {
	userPreferences?: IUserPreferences;
	articleCommentsData?: IArticleCommentsData;
	addArticleComment?: IAddArticleComment;
	deleteArticleComment?: (commentId: string) => IArticleCommentsAction;
	updateArticleCommentInList?: (comment: IArticleComment) => IArticleCommentsAction;
	getArticleComments?: (isFirstLoad: boolean, isAdminPage: boolean, articleId: string) => IArticleCommentsAction;
	reviewArticleComment?: (id: string) => IArticleCommentsAction;
	showModal?: (modalType: string, modalProps: any) => IModalAction;
	closeModal?: () => IModalAction;
	t?: (key: string) => string;
	articleId: string;
	getArticleCommentsSuccess?: (comments: IArticleComment[]) => any;
}

interface IState {
	numOfParentComments: number;
	numOfLoadedComments: number;
}

let gotArticleComments = false;

@connect(
	(state: IStore) => ({
		userPreferences: state.userPreferences,
		articleCommentsData: state.articleComments,
		addArticleComment: state.addArticleCommentReducer,
	}),
	(dispatch) => ({
		deleteArticleComment: (commentId: string) => dispatch(deleteArticleComment(commentId)),
		updateArticleCommentInList: (comment: IArticleComment) => dispatch(updateArticleCommentInList(comment)),
		getArticleComments: (isFirstLoad: boolean, isAdminPage: boolean, articleId: string) =>
			dispatch(getArticleComments(isFirstLoad, isAdminPage, articleId)),
		reviewArticleComment: (commentId: string) => dispatch(reviewArticleComment(commentId)),
		closeModal: () => dispatch(closeModal()),
		showModal: (modalType: string, modalProps: any) => dispatch(showModal(modalType, modalProps)),
		getArticleCommentsSuccess: (comments: IArticleComment[]) => dispatch(getArticleCommentsSuccess(comments)),
	}),
)
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ArticleComments extends React.Component<IProps, IState, {}> {
	constructor(props) {
		super(props);

		this.deleteComment = this.deleteComment.bind(this);
		this.onArticleCommentUpdated = this.onArticleCommentUpdated.bind(this);
		this.loadMoreData = this.loadMoreData.bind(this);
		this.reviewComment = this.reviewComment.bind(this);

		this.state = {
			numOfParentComments: -1,
			numOfLoadedComments: 10,
		};
	}

	public deleteComment(_, itemId: string) {
		if (confirm(this.props.t('Magazine_Article_ArticleComments_DeleteUserMessage'))) {
			this.props.deleteArticleComment(itemId);
		}
	}

	componentDidMount() {
		// console.log("Component mount");
		if (!this.props.articleCommentsData?.isFetching && !gotArticleComments) {
			// if not fetching and we don't have comments yet
			gotArticleComments = true;
			this.props.getArticleComments(true, this.props.articleCommentsData.isAdminPage, this.props.articleId);
		}
	}

	componentDidUpdate() {
		if (
			this.props.articleCommentsData?.isFetching === undefined &&
			!this.props.articleCommentsData?.isFetching &&
			!gotArticleComments
		) {
			gotArticleComments = true;
			this.props.getArticleComments(true, this.props.articleCommentsData.isAdminPage, this.props.articleId);
		}

		if (this.props.addArticleComment.newCommentAdded) {
			this.props.addArticleComment.newCommentAdded = false;
			this.props.getArticleCommentsSuccess([
				// set comments in the redux store
				this.props.addArticleComment.articleComment,
				...this.props.articleCommentsData.comments,
			]);
		}
	}

	componentWillUnmount(): void {
		gotArticleComments = false;
	}

	public onArticleCommentUpdated(comment: IArticleComment) {
		this.props.updateArticleCommentInList(comment);
		this.props.closeModal();
	}

	public loadMoreData() {
		this.props.getArticleComments(false, this.props.articleCommentsData.isAdminPage, this.props.articleId);
	}

	public reviewComment(commentId: string) {
		this.props.reviewArticleComment(commentId);
	}

	public render() {
		const { t } = this.props;

		if (this.props?.articleCommentsData?.error) {
			return <ErrorSummary {...this.props} message={this.props.articleCommentsData.message} />;
		}

		if (this.state.numOfParentComments === -1) {
			// number of parent comments is not set yet (we need it to determine if we need to show the load more comments button)
			if (this.props.articleCommentsData?.comments && this.props.articleCommentsData?.comments.length > 0) {
				let numOfParentComments = 0;
				this.props.articleCommentsData.comments.map((comment) => {
					if (comment.parentId === null) {
						numOfParentComments++;
					}
				});
				this.setState({
					numOfParentComments: numOfParentComments,
				});
			}
		}

		const functions = {
			t: this.props.t,
		};

		const handleLoadMoreComments = () => {
			this.setState({ numOfLoadedComments: this.state.numOfLoadedComments + 10 });
		};

		const replies = {};
		this.props.articleCommentsData.comments?.map((comment) => {
			if (comment?.parentId) {
				if (replies[comment.parentId]) {
					replies[comment.parentId].push(comment);
				} else {
					replies[comment.parentId] = [comment];
				}
			}
		});

		let index = 0;

		return (
			<div className="ArticleComments">
				<h3 className="commentSectionTitle">
					{t('AddComment_Title')}{' '}
					<span className="commentCount">
						{this.props.articleCommentsData?.comments ? this.props.articleCommentsData.comments.length : 0}
					</span>
				</h3>
				<div className="articleCommentsContainer">
					{this.props.articleCommentsData.comments &&
						this.props.articleCommentsData.comments.map((comment) => {
							if (comment.parentId === null && index < this.state.numOfLoadedComments) {
								index++;

								return (
									<ArticleComment
										articleId={this.props.articleId}
										comment={comment}
										key={comment.id}
										functions={functions}
										userPreferences={this.props.userPreferences}
										replies={replies[comment.id]}
									/>
								);
							}
						})}
					{this.state.numOfLoadedComments < this.state.numOfParentComments && (
						<div className="loadMoreComments" onClick={() => handleLoadMoreComments()}>
							<p>Load More Comments {/* TODO: ADD TO RESOURCE FILES */}</p>
						</div>
					)}
				</div>
			</div>
		);
	}
}

export { ArticleComments };
